import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import axios from 'axios';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import NotificationDialog from '../components/NotificationDialog';
import MyPatientsPageActionBar from '../components/myPatients/MyPatientsPageActionBar';
import PatientEditor from '../components/myPatients/PatientEditor';
import { CustomPatientSimulationRulebookButton } from '../components/myPatients/CustomPatientSimulationRulebookButton';
import {
  fieldElementId,
  myPatientPath,
  myPatientsListPath,
  normalizePatientTab,
  PATIENT_TAB_IDS,
  patientLabel,
  validatePatient,
} from '../utils/myPatients';
import { getMyPatient, saveMyPatient } from '../utils/userContentApi';
import {
  clonePatientForHistory,
  commitPatientEditHistory,
  patientsMatchForSave,
  redoPatientEditHistory,
  undoPatientEditHistory,
} from '../utils/patientEditHistory';

const FORM_ID = 'my-patient-form';

function focusField(id) {
  if (!id) return;
  requestAnimationFrame(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    el.focus();
  });
}

const MyPatientDetail = () => {
  const navigate = useNavigate();
  const { patientId: rawPatientId, tab: rawTab } = useParams();
  const patientId = decodeURIComponent(rawPatientId || '');
  const tab = normalizePatientTab(rawTab);

  const [patients, setPatients] = useState([]);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [errors, setErrors] = useState({});
  const [dialog, setDialog] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [lastSavedPatient, setLastSavedPatient] = useState(null);
  const pendingFocus = useRef('');
  const focusNameFor = useRef('');
  const patientsRef = useRef(patients);
  const dirtyRef = useRef(false);
  const committedPatientRef = useRef(null);
  const patientHistoryRef = useRef({ past: [], future: [] });
  patientsRef.current = patients;

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    getMyPatient(patientId)
      .then((patient) => {
        if (cancelled) return;
        const next = patient?.id ? [patient] : [];
        patientsRef.current = next;
        dirtyRef.current = false;
        committedPatientRef.current = patient?.id ? clonePatientForHistory(patient) : null;
        patientHistoryRef.current = { past: [], future: [] };
        setLastSavedPatient(patient?.id ? clonePatientForHistory(patient) : null);
        setPatients(next);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message || 'The patient could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const selected = useMemo(
    () => patients.find((patient) => patient.id === patientId) || null,
    [patients, patientId]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!selected || !lastSavedPatient) return false;
    return !patientsMatchForSave(selected, lastSavedPatient);
  }, [lastSavedPatient, selected]);

  const markPatientSaved = useCallback((patient) => {
    dirtyRef.current = false;
    setLastSavedPatient(clonePatientForHistory(patient));
  }, []);

  useEffect(() => {
    if (!dirtyRef.current) return undefined;
    const handle = setTimeout(() => {
      const current = patientsRef.current.find((patient) => patient.id === patientId);
      if (!current || validatePatient(current).firstKey) return;
      saveMyPatient(patientId, current)
        .then(() => {
          markPatientSaved(current);
        })
        .catch((error) => {
          setDialog({ kind: 'error', message: error.message || 'The patient could not be saved.' });
        });
    }, 700);
    return () => clearTimeout(handle);
  }, [markPatientSaved, patients, patientId]);

  useEffect(() => {
    if (!focusNameFor.current) return undefined;
    const id = focusNameFor.current;
    focusNameFor.current = '';
    focusField(id);
    return undefined;
  }, [patients]);

  const applyPatientSnapshot = useCallback((snapshot) => {
    const cloned = clonePatientForHistory(snapshot);
    const updated = patientsRef.current.map((patient) => (
      patient.id === cloned.id ? cloned : patient
    ));
    patientsRef.current = updated;
    dirtyRef.current = true;
    setPatients(updated);
    setErrors({});
  }, []);

  const commitPatientEdit = useCallback(() => {
    const current = patientsRef.current.find((patient) => patient.id === patientId);
    if (!current || !committedPatientRef.current) return false;
    const result = commitPatientEditHistory(
      patientHistoryRef.current,
      committedPatientRef.current,
      current,
    );
    if (!result.changed) return false;
    patientHistoryRef.current = result.history;
    committedPatientRef.current = result.committed;
    return true;
  }, [patientId]);

  const restorePatientFromHistory = useCallback((snapshot) => {
    const cloned = clonePatientForHistory(snapshot);
    committedPatientRef.current = cloned;
    applyPatientSnapshot(cloned);
  }, [applyPatientSnapshot]);

  const undoPatientEdit = useCallback(() => {
    if (!committedPatientRef.current) return false;
    const result = undoPatientEditHistory(
      patientHistoryRef.current,
      committedPatientRef.current,
    );
    if (!result) return false;
    patientHistoryRef.current = result.history;
    restorePatientFromHistory(result.patient);
    return true;
  }, [restorePatientFromHistory]);

  const redoPatientEdit = useCallback(() => {
    if (!committedPatientRef.current) return false;
    const result = redoPatientEditHistory(
      patientHistoryRef.current,
      committedPatientRef.current,
    );
    if (!result) return false;
    patientHistoryRef.current = result.history;
    restorePatientFromHistory(result.patient);
    return true;
  }, [restorePatientFromHistory]);

  useEffect(() => {
    const handleKeyboardShortcut = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        commitPatientEdit();
        undoPatientEdit();
        return;
      }
      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        redoPatientEdit();
      }
    };

    window.addEventListener('keydown', handleKeyboardShortcut, true);
    return () => window.removeEventListener('keydown', handleKeyboardShortcut, true);
  }, [commitPatientEdit, redoPatientEdit, undoPatientEdit]);

  const handleFormBlurCapture = useCallback(() => {
    window.setTimeout(commitPatientEdit, 0);
  }, [commitPatientEdit]);

  const handleBlur = useCallback((key) => {
    const current = patientsRef.current.find((patient) => patient.id === patientId);
    if (!current) return;
    const result = validatePatient(current);
    setErrors((prev) => {
      const copy = { ...prev };
      if (result.errors[key]) copy[key] = result.errors[key];
      else delete copy[key];
      return copy;
    });
    commitPatientEdit();
  }, [commitPatientEdit, patientId]);

  const updateSelected = useCallback((next, errorKey) => {
    const updated = patientsRef.current.map((patient) => (patient.id === next.id ? next : patient));
    patientsRef.current = updated;
    dirtyRef.current = true;
    setPatients(updated);
    if (!errorKey) return;
    setErrors((prev) => {
      if (!prev[errorKey]) return prev;
      const copy = { ...prev };
      delete copy[errorKey];
      return copy;
    });
  }, []);

  const handleTabChange = useCallback((nextTab) => {
    navigate(myPatientPath(patientId, nextTab));
  }, [navigate, patientId]);

  const handleSave = useCallback(() => {
    const current = patientsRef.current.find((patient) => patient.id === patientId);
    if (!current) return;
    if (lastSavedPatient && patientsMatchForSave(current, lastSavedPatient)) return;
    commitPatientEdit();
    const result = validatePatient(current);
    if (result.firstKey) {
      setErrors(result.errors);
      navigate(myPatientPath(patientId, result.tab || 'personal'));
      pendingFocus.current = fieldElementId(current.id, result.firstKey);
      setDialog({ kind: 'invalid', errors: result.errors });
      return;
    }
    saveMyPatient(patientId, current)
      .then(() => {
        markPatientSaved(current);
        setErrors({});
        setDialog({ kind: 'saved', name: patientLabel(current) });
      })
      .catch((error) => {
        setDialog({ kind: 'error', message: error.message || 'The patient could not be saved.' });
      });
  }, [commitPatientEdit, lastSavedPatient, markPatientSaved, navigate, patientId]);

  const handleSimulate = useCallback(async () => {
    const current = patientsRef.current.find((patient) => patient.id === patientId);
    if (!current) return;
    commitPatientEdit();
    const result = validatePatient(current);
    if (result.firstKey) {
      setErrors(result.errors);
      navigate(myPatientPath(patientId, result.tab || 'personal'));
      pendingFocus.current = fieldElementId(current.id, result.firstKey);
      setDialog({ kind: 'invalid', errors: result.errors });
      return;
    }

    setSimulating(true);
    try {
      if (!lastSavedPatient || !patientsMatchForSave(current, lastSavedPatient)) {
        await saveMyPatient(patientId, current);
        markPatientSaved(current);
        setErrors({});
      }

      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post('/api/simulation/start', {
        mode: 'medical_diagnosis',
        customPatientId: patientId,
      }, { headers });

      if (!response.data?.sessionId) {
        throw new Error('No session ID returned from server');
      }
      navigate(`/simulation/${response.data.sessionId}`);
    } catch (error) {
      const message = error.response?.data?.error
        || error.message
        || 'Simulation could not be started.';
      setDialog({ kind: 'error', message });
    } finally {
      setSimulating(false);
    }
  }, [commitPatientEdit, lastSavedPatient, markPatientSaved, navigate, patientId]);

  if (!ready) {
    return (
      <PageLayout maxWidth="xl">
        <Typography variant="body2" color="text.secondary">Loading patient…</Typography>
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout maxWidth="xl">
        <Typography variant="body1">{loadError}</Typography>
      </PageLayout>
    );
  }

  if (!selected) {
    return <Navigate to={myPatientsListPath()} replace />;
  }

  if (rawTab && !PATIENT_TAB_IDS.includes(rawTab.trim().toLowerCase())) {
    return <Navigate to={myPatientPath(patientId, 'personal')} replace />;
  }

  const closeDialog = () => {
    const focusId = dialog?.kind === 'invalid' ? pendingFocus.current : '';
    pendingFocus.current = '';
    setDialog(null);
    if (focusId) focusField(focusId);
  };

  const dialogProps = (() => {
    if (!dialog) {
      return { open: false, title: '', message: '', buttons: ['dismiss'] };
    }
    if (dialog.kind === 'saved') {
      return {
        open: true,
        title: 'Patient saved',
        message: `${dialog.name} is saved to your account.`,
        iconPreset: 'info',
        buttons: ['dismiss'],
        buttonLabels: { dismiss: 'OK' },
      };
    }
    if (dialog.kind === 'error') {
      return {
        open: true,
        title: 'Something went wrong',
        message: dialog.message,
        iconPreset: 'warning',
        buttons: ['dismiss'],
      };
    }
    const entries = Object.entries(dialog.errors || {});
    return {
      open: true,
      title: 'Check these fields',
      iconPreset: 'warning',
      buttons: ['dismiss'],
      buttonLabels: { dismiss: 'Review fields' },
      children: (
        <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
          {entries.map(([key, message]) => (
            <Typography component="li" key={key} variant="body1" sx={{ mb: 0.5 }}>
              {message}
            </Typography>
          ))}
        </Stack>
      ),
    };
  })();

  return (
    <PageLayout
      maxWidth="xl"
      sx={{
        pb: `calc(128px + env(safe-area-inset-bottom, 0px))`,
      }}
    >
      <PageHeader
        title={patientLabel(selected)}
        subtitle="Personal details, health history, and pathology. Changes save to your account."
        showBackButton
        backPath={myPatientsListPath()}
        actions={<CustomPatientSimulationRulebookButton />}
        sx={{ mb: 2 }}
      />

      <PatientEditor
        key={selected.id}
        patient={selected}
        tab={tab}
        onTabChange={handleTabChange}
        errors={errors}
        onChange={updateSelected}
        onBlurField={handleBlur}
        onCommitEdit={commitPatientEdit}
        onFormBlurCapture={handleFormBlurCapture}
        formId={FORM_ID}
        onSave={handleSave}
      />

      <MyPatientsPageActionBar
        formId={FORM_ID}
        saveDisabled={!hasUnsavedChanges}
        extra={(
          <Button
            type="button"
            variant="outlined"
            startIcon={<PlayArrowIcon />}
            disabled={simulating}
            onClick={handleSimulate}
            sx={{ minHeight: 44 }}
          >
            {simulating ? 'Starting…' : 'Simulate'}
          </Button>
        )}
      />

      <NotificationDialog
        open={dialogProps.open}
        onClose={closeDialog}
        title={dialogProps.title}
        message={dialogProps.message}
        iconPreset={dialogProps.iconPreset}
        buttons={dialogProps.buttons}
        buttonLabels={dialogProps.buttonLabels}
        onAction={dialogProps.onAction}
      >
        {dialogProps.children}
      </NotificationDialog>
    </PageLayout>
  );
};

export default MyPatientDetail;
